/* eslint-disable @typescript-eslint/no-floating-promises */
import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { IdentityServiceHandler } from '@/core/apis/identity_api';
import { CommonSessionAndOrBrowserResponse } from '@/core/apis/identity_api/types';
import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import {
  TBrowserData,
  TEventSessionData,
  TMarketIdentifiers,
  TPlatformIdentifiers,
} from '@/core/constants/event_types/base_mable_event';
import { UniqueIdUtils } from '@/core/event_logic/utils/unique_id_utils';
import { Logger } from '@/shared/logger/logger';

import { BrowserDataHandler } from '../browser_data';
import { UnifiedCustomerDataHandler } from '../customer_data_new';
import {
  ICustomerDataFields,
  TCustomerDataArrays,
} from '../customer_data_new/types';
import { IdentityStateManager } from '../identity_state_manager';
import { IpDataHandler } from '../ip_data';
import { IpData } from '../ip_data/types';
import { PlatformIdentifiersHandler } from '../platform_identifier_data';
import { SessionDataHandler } from '../session_data';
import { SessionLogicHandler } from '../session_data/session_logic';
import { StorageHandler } from '../storage_handler';

import { UaHeaders } from '../../constants/event_types/base_mable_event';

const IDENTITY_UPDATE_ACTIVE = true;

export type BrowserInstanceData = {
  bd: TBrowserData;
  cdas: TCustomerDataArrays;
  ii: string;
  pids: TPlatformIdentifiers;
  sd: TEventSessionData;
  uaHeaders: UaHeaders;
};

export class BrowserInstanceHandler {
  private static readonly _storageHandler = new StorageHandler<{ id: string }>(
    'identityId',
    { id: '' },
    EBrowserStorageType.LOCALSTORAGE,
  );
  private static readonly unifiedCdDataHandler = UnifiedCustomerDataHandler;

  static hasIdentity(): boolean {
    const identity = this._storageHandler.get();
    return identity.id !== '';
  }

  static loadOrInitIdentityId(): { foundExisting: boolean; id: string } {
    const identity = this._storageHandler.get();
    Logger.debug('GOT identity', identity, identity.id ? 'FOUND' : 'NOT FOUND');
    if (!identity.id) {
      identity.id = UniqueIdUtils.getUuidV7();
      this._storageHandler.set(identity);
      return { foundExisting: false, id: identity.id };
    }
    return { foundExisting: true, id: identity.id };
  }

  // Original implementation preserved as a private method for testing/comparison
  static async _originalInitOrLoadAndMaybeUpdate(
    browserApiData: TBrowserApiData,
    ipData: IpData,
    mktData: TMarketIdentifiers,
  ): Promise<BrowserInstanceData> {
    // First load identity ID since other operations depend on it
    const { foundExisting, id } = this.loadOrInitIdentityId();
    const newBrowser = !foundExisting;

    // Check if a new session should start
    const newSession = await SessionLogicHandler.DoesNewSessionStart();

    // Parallelize independent async operations
    const [sessionResult, browserResult, ipLocationResult] = await Promise.all([
      SessionDataHandler.initOrLoadAndMaybeUpdate(
        browserApiData,
        ipData.ipAddress,
        newSession,
        mktData,
      ),
      BrowserDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
      this.unifiedCdDataHandler.maybeUpdateWithIpLocation(ipData.ipLocation),
    ]);

    // This operation doesn't need to be awaited, so can be executed in parallel
    const pidsResult =
      PlatformIdentifiersHandler.initOrLoadAndMaybeUpdate(browserApiData);

    // Update session data with identity and platform data
    sessionResult.sessionData = await SessionDataHandler.updateIdentityData(
      ipLocationResult.currentCustomerSessionData,
      pidsResult.platformIdentifiers,
    );

    Logger.debug('sessionResult', { browserResult, sessionResult });

    // Handle identity update based on collected data
    const identityResult = await this.handleIdentityUpdate(
      newSession,
      newBrowser,
      id,
      sessionResult,
      browserResult,
    );

    // Return appropriate browser instance data
    return identityResult &&
      identityResult.identityUpdateDetected &&
      IDENTITY_UPDATE_ACTIVE
      ? this.buildUpdatedBrowserInstanceData(
          identityResult,
          sessionResult,
          browserResult,
          pidsResult,
          id,
          ipData?.uaHeaders || {},
        )
      : this.buildBrowserInstanceData(
          sessionResult,
          browserResult,
          pidsResult,
          id,
          ipData?.uaHeaders || {},
        );
  }

  // New implementation that doesn't block on identity service calls
  static async initOrLoadAndMaybeUpdate(
    browserApiData: TBrowserApiData,
    ipData: IpData,
    mktData: TMarketIdentifiers,
  ): Promise<BrowserInstanceData> {
    // First load identity ID since other operations depend on it
    const { foundExisting, id } = this.loadOrInitIdentityId();
    const newBrowser = !foundExisting;

    // Check if a new session should start
    const newSession = await SessionLogicHandler.DoesNewSessionStart();

    // Parallelize independent async operations
    const [sessionResult, browserResult, ipLocationResult] = await Promise.all([
      SessionDataHandler.initOrLoadAndMaybeUpdate(
        browserApiData,
        ipData.ipAddress,
        newSession,
        mktData,
      ),
      BrowserDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
      this.unifiedCdDataHandler.maybeUpdateWithIpLocation(ipData.ipLocation),
    ]);

    // This operation doesn't need to be awaited, so can be executed in parallel
    const pidsResult =
      PlatformIdentifiersHandler.initOrLoadAndMaybeUpdate(browserApiData);

    // Update session data with identity and platform data
    sessionResult.sessionData = await SessionDataHandler.updateIdentityData(
      ipLocationResult.currentCustomerSessionData,
      pidsResult.platformIdentifiers,
    );

    Logger.debug('sessionResult', { browserResult, sessionResult });

    // Create initial browser instance data without waiting for identity service
    const initialBrowserInstanceData = await this.buildBrowserInstanceData(
      sessionResult,
      browserResult,
      pidsResult,
      id,
      ipData?.uaHeaders || {},
    );

    // Store the initial state
    IdentityStateManager.getInstance().setState(initialBrowserInstanceData);

    // Trigger background identity update without awaiting it
    this.performBackgroundIdentityUpdate(
      newSession,
      newBrowser,
      id,
      sessionResult,
      browserResult,
      pidsResult,
      ipData?.uaHeaders || {},
    );

    // Return initial data immediately
    return initialBrowserInstanceData;
  }

  /**
   * Perform identity updates in the background without blocking execution
   * @private
   */
  private static async performBackgroundIdentityUpdate(
    newSession: boolean,
    newBrowser: boolean,
    id: string,
    sessionResult: any,
    browserResult: any,
    pidsResult: any,
    uaHeaders: UaHeaders,
  ): Promise<void> {
    try {
      // Handle identity update based on collected data
      const identityResult = await this.handleIdentityUpdate(
        newSession,
        newBrowser,
        id,
        sessionResult,
        browserResult,
      );

      // If there's an update, build updated data and update state
      if (
        identityResult &&
        identityResult.identityUpdateDetected &&
        IDENTITY_UPDATE_ACTIVE
      ) {
        const updatedData = await this.buildUpdatedBrowserInstanceData(
          identityResult,
          sessionResult,
          browserResult,
          pidsResult,
          id,
          uaHeaders,
        );

        // Update the state
        IdentityStateManager.getInstance().setState(updatedData);
        Logger.debug('Background identity update completed', {
          identityId: id,
        });
      }
    } catch (error) {
      Logger.error('Background identity update failed', error);
    }
  }

  private static async handleIdentityUpdate(
    newSession: boolean,
    newBrowser: boolean,
    id: string,
    sessionResult: any,
    browserResult: any,
  ): Promise<CommonSessionAndOrBrowserResponse> {
    if (newSession || newBrowser) {
      return IdentityServiceHandler.createNewSessionAndOrBrowserData({
        browserChanged: browserResult.browserDataChanged,
        browserData: browserResult.browserData,
        identityIdentifier: id,
        sessionData: sessionResult.sessionData,
      });
    }

    if (sessionResult.sessionDataChanged || browserResult.browserDataChanged) {
      return IdentityServiceHandler.updatedSessionAndOrBrowserData({
        browserChanged: browserResult.browserDataChanged,
        browserData: browserResult.browserData,
        identityIdentifier: id,
        sessionChanged: sessionResult.sessionDataChanged,
        sessionData: sessionResult.sessionData,
      });
    }

    return {
      identityUpdateDetected: false,
      success: true,
      updatededIi: id,
      updatedIdentity: {
        cdfs: [],
        pids: { epik: [], fbclid: [], gclid: [], ttclid: [] },
      },
    };
  }

  private static async handleUpdatedIdentity(
    identityResult: CommonSessionAndOrBrowserResponse,
    pidsResult: any,
  ) {
    const { platformIdentifiers, platformIdentifiersChanged } =
      PlatformIdentifiersHandler.updateFromIdentity(
        identityResult.updatedIdentity.pids,
      );
    if (platformIdentifiersChanged)
      pidsResult.platformIdentifiers = platformIdentifiers;

    const { mergedCustomerData } = this.unifiedCdDataHandler.updateIdentity(
      identityResult.updatedIdentity.cdfs,
    );
    return { mergedCustomerData, platformIdentifiers };
  }

  private static async buildUpdatedBrowserInstanceData(
    identityResult: CommonSessionAndOrBrowserResponse,
    sessionResult: any,
    browserResult: any,
    pidsResult: any,
    id: string,
    uaHeaders: UaHeaders,
  ): Promise<BrowserInstanceData> {
    const { mergedCustomerData, platformIdentifiers } =
      await this.handleUpdatedIdentity(identityResult, pidsResult);
    return {
      bd: browserResult.browserData,
      cdas: mergedCustomerData,
      ii: id,
      pids: platformIdentifiers,
      sd: sessionResult.sessionData,
      uaHeaders,
    };
  }

  private static async buildBrowserInstanceData(
    sessionResult: any,
    browserResult: any,
    pidsResult: any,
    id: string,
    uaHeaders: UaHeaders,
  ): Promise<BrowserInstanceData> {
    const { mergedCustomerData } = this.unifiedCdDataHandler.initOrMaybeUpdate(
      [],
    );
    return {
      bd: browserResult.browserData,
      cdas: mergedCustomerData,
      ii: id,
      pids: pidsResult.platformIdentifiers,
      sd: sessionResult.sessionData,
      uaHeaders,
    };
  }

  // Original implementation preserved as a private method for testing/comparison
  static async _originalLoadAndMaybeUpdate(
    customerData: ICustomerDataFields[],
    browserApiData: TBrowserApiData,
  ): Promise<BrowserInstanceData> {
    // Load identity ID and IP data in parallel
    const [ipData, identityIdResult] = await Promise.all([
      IpDataHandler.initOrLoad(),
      Promise.resolve(this.loadOrInitIdentityId()),
    ]);
    const { id } = identityIdResult;

    // Parallelize independent async operations
    const [sessionResult, browserResult] = await Promise.all([
      SessionDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
      BrowserDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
    ]);

    // These operations don't need to be awaited, so can be executed simultaneously
    const pidsResult =
      PlatformIdentifiersHandler.initOrLoadAndMaybeUpdate(browserApiData);
    const customerResult =
      this.unifiedCdDataHandler.initOrMaybeUpdate(customerData);

    // Update session data with customer and platform data
    sessionResult.sessionData = await SessionDataHandler.updateIdentityData(
      customerResult.newSessionData,
      pidsResult.platformIdentifiers,
    );

    // Update session data change status
    sessionResult.sessionDataChanged =
      sessionResult.sessionDataChanged ||
      customerResult.newDataAdded ||
      pidsResult.platformIdentifiersChanged;

    // Skip identity update if not active
    if (!IDENTITY_UPDATE_ACTIVE) {
      return this.buildBrowserInstanceData(
        sessionResult,
        browserResult,
        pidsResult,
        id,
        ipData?.uaHeaders || {},
      );
    }

    // Handle identity update based on collected data
    const identityResult = await this.handleIdentityUpdate(
      false,
      false,
      id,
      sessionResult,
      browserResult,
    );

    // Return appropriate browser instance data
    return identityResult?.identityUpdateDetected
      ? this.buildUpdatedBrowserInstanceData(
          identityResult,
          sessionResult,
          browserResult,
          pidsResult,
          id,
          ipData?.uaHeaders || {},
        )
      : this.buildBrowserInstanceData(
          sessionResult,
          browserResult,
          pidsResult,
          id,
          ipData?.uaHeaders || {},
        );
  }

  // Async optimized version
  static async loadAndMaybeUpdate(
    customerData: ICustomerDataFields[],
    browserApiData: TBrowserApiData,
  ): Promise<BrowserInstanceData> {
    // Load identity ID and IP data in parallel
    const [ipData, identityIdResult] = await Promise.all([
      IpDataHandler.initOrLoad(),
      Promise.resolve(this.loadOrInitIdentityId()),
    ]);
    const { id } = identityIdResult;

    // Parallelize independent async operations
    const [sessionResult, browserResult] = await Promise.all([
      SessionDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
      BrowserDataHandler.initOrLoadAndMaybeUpdate(browserApiData),
    ]);

    // These operations must be awaited now since we need their results immediately
    const pidsResult =
      PlatformIdentifiersHandler.initOrLoadAndMaybeUpdate(browserApiData);
    const customerResult =
      this.unifiedCdDataHandler.initOrMaybeUpdate(customerData);

    // Update session data with customer and platform data
    sessionResult.sessionData = await SessionDataHandler.updateIdentityData(
      customerResult.newSessionData,
      pidsResult.platformIdentifiers,
    );

    // Update session data change status
    sessionResult.sessionDataChanged =
      sessionResult.sessionDataChanged ||
      customerResult.newDataAdded ||
      pidsResult.platformIdentifiersChanged;

    // Create initial browser instance data
    const initialBrowserInstanceData = await this.buildBrowserInstanceData(
      sessionResult,
      browserResult,
      pidsResult,
      id,
      ipData?.uaHeaders || {},
    );

    // Store the initial state
    IdentityStateManager.getInstance().setState(initialBrowserInstanceData);

    // Skip identity update if not active
    if (!IDENTITY_UPDATE_ACTIVE) {
      return initialBrowserInstanceData;
    }

    // Perform identity update in the background
    this.performBackgroundLoadAndMaybeUpdate(
      id,
      sessionResult,
      browserResult,
      pidsResult,
      ipData?.uaHeaders || {},
    );

    // Return initial data immediately
    return initialBrowserInstanceData;
  }

  /**
   * Background version of identity updates for loadAndMaybeUpdate
   * @private
   */
  private static async performBackgroundLoadAndMaybeUpdate(
    id: string,
    sessionResult: any,
    browserResult: any,
    pidsResult: any,
    uaHeaders: UaHeaders,
  ): Promise<void> {
    try {
      // Handle identity update based on collected data
      const identityResult = await this.handleIdentityUpdate(
        false,
        false,
        id,
        sessionResult,
        browserResult,
      );

      // If there's an update, build updated data and update state
      if (identityResult?.identityUpdateDetected) {
        const updatedData = await this.buildUpdatedBrowserInstanceData(
          identityResult,
          sessionResult,
          browserResult,
          pidsResult,
          id,
          uaHeaders,
        );

        // Update the state
        IdentityStateManager.getInstance().setState(updatedData);
        Logger.debug('Background customer identity update completed', {
          identityId: id,
        });
      }
    } catch (error) {
      Logger.error('Background customer identity update failed', error);
    }
  }
}
